import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import { LocationIdSchema } from '@brightlocal/shared';

const ScoreValueSchema = z.object({
  // Null while the ai_insights report is still generating (status initiated/in_progress) — scores are not computed yet.
  current: z.number().nullable(),
  // Null on the first ai_insights report — there is no prior run to compare the current value against.
  previous: z.number().nullable()
});

const DateValueSchema = z.object({
  // Null while the ai_insights report is still generating (status initiated/in_progress).
  current: z.string().nullable(),
  // Null on the first ai_insights report — there is no prior run date to compare against.
  previous: z.string().nullable()
});

const ApiAiInsightsMetricsSchema = z.object({
  insights_count: z.number(),
  actions_count: z.number()
});

const ApiAiInsightsContentSchema = z.object({
  // Null while the ai_insights report is still generating (status initiated/in_progress) — the summary text is not written yet.
  executive_summary: z.string().nullable(),
  // Absent when the location has no previous-run comparison data yet (e.g. first ai_insights report).
  run_date: DateValueSchema.optional(),
  score: z
    .object({
      location_score: ScoreValueSchema,
      foundation: z.object({
        gbp: ScoreValueSchema,
        website_and_content: ScoreValueSchema,
        citations: ScoreValueSchema,
        reviews: ScoreValueSchema
      }),
      visibility: z.object({
        google_maps: ScoreValueSchema,
        organic_search: ScoreValueSchema
      })
    })
    .optional()
});

const ApiBaseReportSummarySchema = z.object({
  id: z.string(),
  report_id: z.string(),
  report_type: z.string(),
  report_status: z.string(),
  report_creation_date: z.string()
});

const ApiAiInsightsSummarySchema = ApiBaseReportSummarySchema.extend({
  report_type: z.literal('ai_insights'),
  metrics: ApiAiInsightsMetricsSchema,
  content: ApiAiInsightsContentSchema
});

const ApiRmMetricsSchema = z.object({
  total_reviews: z.number(),
  new_reviews: z.number(),
  average_star_rating: z.number(),
  average_rating_change: z.number()
});

const ApiRmTopReviewSiteSchema = z.object({
  name: z.string(),
  review_count: z.number(),
  average_rating: z.number()
});

const ApiRmContentSchema = z.object({
  top_review_sites: z.array(ApiRmTopReviewSiteSchema)
});

const ApiRmSummarySchema = ApiBaseReportSummarySchema.extend({
  report_type: z.literal('rm'),
  // Documented on the per-location endpoint but may be omitted in practice.
  location_id: LocationIdSchema.optional(),
  metrics: ApiRmMetricsSchema,
  content: ApiRmContentSchema
});

const KNOWN_REPORT_TYPES: string[] = [
  ApiAiInsightsSummarySchema.shape.report_type.value,
  ApiRmSummarySchema.shape.report_type.value
];

// An open map whose keys/values we don't model. The API serialises it as an empty
// array (`[]`) rather than an empty object when it has no entries, so accept both shapes.
const ApiOpenMapSchema = z.union([z.record(z.unknown()), z.array(z.unknown())]);

// Catch-all for future report types not yet modelled — keeps metrics/content as open maps.
// Explicitly excludes known discriminated types so malformed known payloads can't bypass strict validation.
const ApiUnknownReportSummarySchema = ApiBaseReportSummarySchema.extend({
  report_type: z.string().refine((val) => !KNOWN_REPORT_TYPES.includes(val)),
  metrics: ApiOpenMapSchema,
  content: ApiOpenMapSchema
});

const ApiReportSummarySchema = z.union([ApiAiInsightsSummarySchema, ApiRmSummarySchema, ApiUnknownReportSummarySchema]);

/** Validates and transforms the API response for the location report summaries endpoint. */
export const DetailedLocationSummaryResponseSchema = z
  .object({
    data: z.array(ApiReportSummarySchema)
  })
  .transform((response) => camelcaseKeys(response, { deep: true }));

/** Shape of the location report summaries response after camelCase transformation. */
export type DetailedLocationSummaryResponse = z.infer<typeof DetailedLocationSummaryResponseSchema>;

/** A single report summary item after camelCase transformation. */
export type DetailedLocationSummary = DetailedLocationSummaryResponse['data'][number];

/** Report type discriminator values as defined by the API contract. */
export const LOCATION_SUMMARY_REPORT_TYPES = {
  AI_INSIGHTS: ApiAiInsightsSummarySchema.shape.report_type.value,
  RM: ApiRmSummarySchema.shape.report_type.value
} as const;
